import React, { useEffect, useState } from 'react';
import { Attempt, Bookmark, User, AIAnalysis, Exam } from '../types';
import { fetchUserAttempts, fetchBookmarks, toggleBookmark } from '../services/dataService';
import { analyzeUserPerformance } from '../services/geminiService';
import { useToast } from './Toast';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface UserDashboardProps {
  user: User;
  onStartExam: () => void;
  onStartCustomExam?: () => void;
  onStartCustomExamInstance?: (exam: Exam) => void;
}

const UserDashboard: React.FC<UserDashboardProps> = ({ user, onStartExam, onStartCustomExam, onStartCustomExamInstance }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'overview' | 'bookmarks'>('overview');
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);

  // AI Analysis State
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisData, setAnalysisData] = useState<AIAnalysis | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [attemptsData, bookmarksData] = await Promise.all([
        fetchUserAttempts(user.id),
        fetchBookmarks(user.id)
      ]);
      setAttempts(attemptsData);
      setBookmarks(bookmarksData);
    } catch (error) {
      console.error("Failed to load dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user.id]);

  const handleRemoveBookmark = async (question: any, examId: string) => {
    await toggleBookmark(user.id, question, examId);
    setBookmarks(prev => prev.filter(b => b.questionId !== question.id));
  };

  const handleLaunchBookmarksPractice = () => {
    if (bookmarks.length === 0 || !onStartCustomExamInstance) {
      showToast('info', 'No bookmarked questions to practice.');
      return;
    }

    const bookmarkQuestions = bookmarks.map((b) => b.questionData);

    const bookmarkExam: Exam = {
      id: `bookmark-practice-${Date.now()}`,
      title: 'Saved Questions Practice Mock',
      category: 'bookmarks',
      durationMinutes: Math.max(5, Math.ceil(bookmarkQuestions.length * 1.5)),
      totalQuestions: bookmarkQuestions.length,
      questions: bookmarkQuestions,
      positiveMarks: 2,
      negativeMarks: 0.66
    };

    onStartCustomExamInstance(bookmarkExam);
  };

  const handleRunAnalysis = async () => {
    if (user.subscriptionTier === 'free') {
      showToast('warning', 'Upgrade to Premium to access AI Analysis!');
      return;
    }
    if (attempts.length < 2) {
      showToast('info', 'Please take at least 2 tests to generate a trend analysis.');
      return;
    }

    setShowAnalysis(true);
    setAnalyzing(true);
    try {
      const data = await analyzeUserPerformance(attempts, user.name || 'Aspirant');
      setAnalysisData(data);
    } catch (error) {
      showToast('error', 'Analysis failed. Please try again.');
      setShowAnalysis(false);
    } finally {
      setAnalyzing(false);
    }
  };

  const stats = {
    totalTests: attempts.length,
    avgScore: attempts.length > 0
      ? (attempts.reduce((acc, curr) => acc + (curr.score / (curr.totalQuestions * 2) * 100), 0) / attempts.length).toFixed(1)
      : 0,
    totalQuestionsSolved: attempts.reduce((acc, curr) => acc + curr.totalQuestions, 0),
    accuracy: attempts.length > 0
      ? (attempts.reduce((acc, curr) => acc + (curr.correctCount / (curr.correctCount + curr.wrongCount || 1) * 100), 0) / attempts.length).toFixed(1)
      : 0
  };

  const statCards = [
    { label: 'Tests Taken', value: stats.totalTests, icon: 'fa-file-alt', color: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50 dark:bg-blue-900/30' },
    { label: 'Avg. Score', value: `${stats.avgScore}%`, icon: 'fa-chart-line', color: 'from-purple-500 to-indigo-500', bg: 'bg-purple-50 dark:bg-purple-900/30' },
    { label: 'Questions Solved', value: stats.totalQuestionsSolved, icon: 'fa-check-double', color: 'from-orange-500 to-red-500', bg: 'bg-orange-50 dark:bg-orange-900/30' },
    { label: 'Accuracy', value: `${stats.accuracy}%`, icon: 'fa-bullseye', color: 'from-green-500 to-emerald-500', bg: 'bg-green-50 dark:bg-green-900/30' },
  ];

  // Map attempts to chart data points
  const chartData = [...attempts]
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .map((att, index) => ({
      name: att.examTitle || `Test ${index + 1}`,
      percentage: Math.max(0, Math.round((att.score / (att.totalQuestions * 2)) * 100)),
      date: new Date(att.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }));

  return (
    <div className="min-h-screen">
      {/* Hero Welcome Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-90"></div>
        <div className="absolute inset-0 mesh-gradient"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="animate-slide-up">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center text-white text-2xl font-bold border border-white/20">
                  {(user.name || user.email)[0].toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="font-display text-3xl lg:text-4xl font-bold text-white">
                      Welcome back, {user.name || 'Aspirant'}!
                    </h1>
                    {user.subscriptionTier !== 'free' && (
                      <span className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs px-3 py-1 rounded-full uppercase font-bold tracking-wider shadow-lg shadow-purple-500/30 flex items-center animate-pulse-slow">
                        <i className="fas fa-crown mr-1.5"></i> {user.subscriptionTier}
                      </span>
                    )}
                  </div>
                  <p className="text-white/60 mt-1">
                    {user.targetExam ? `Targeting ${user.targetExam}` : 'Ready to ace your exams today?'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <button
                onClick={onStartExam}
                className="btn-primary text-white px-6 py-3.5 rounded-xl font-bold inline-flex items-center group"
              >
                <i className="fas fa-play-circle mr-2 group-hover:scale-110 transition-transform"></i>
                Take a New Test
              </button>
              {user.subscriptionTier !== 'free' && onStartCustomExam && (
                <button
                  onClick={onStartCustomExam}
                  className="glass text-white px-6 py-3.5 rounded-xl font-bold inline-flex items-center hover:bg-white/20 transition-all"
                >
                  <i className="fas fa-magic mr-2"></i>
                  Custom Test
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Premium Feature Cards */}
        {user.subscriptionTier !== 'free' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 -mt-16 relative z-10">
            <div
              onClick={onStartCustomExam}
              className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-200 dark:border-slate-700 cursor-pointer card-hover relative overflow-hidden group shadow-glass"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-indigo-500/5 group-hover:from-purple-500/10 group-hover:to-indigo-500/10 transition-all"></div>
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative z-10 flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center text-white text-xl shadow-lg shadow-purple-500/30">
                  <i className="fas fa-magic"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mb-2">Custom Test Generator</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">Create unlimited tests by mixing subjects and difficulty levels.</p>
                  <span className="text-purple-600 dark:text-purple-400 font-semibold text-sm flex items-center group-hover:translate-x-1 transition-transform">
                    Create Now <i className="fas fa-arrow-right ml-2"></i>
                  </span>
                </div>
              </div>
            </div>

            <div
              onClick={handleRunAnalysis}
              className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-200 dark:border-slate-700 relative overflow-hidden cursor-pointer card-hover group shadow-glass"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-red-500/5 group-hover:from-orange-500/10 group-hover:to-red-500/10 transition-all"></div>
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br from-orange-500 to-red-500 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative z-10 flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center text-white text-xl shadow-lg shadow-orange-500/30">
                  <i className="fas fa-brain"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mb-2">AI Performance Analysis</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">Get a detailed SWOT analysis and personalized study plan.</p>
                  <span className="text-orange-600 dark:text-orange-400 font-semibold text-sm flex items-center group-hover:translate-x-1 transition-transform">
                    <i className="fas fa-bolt mr-1 text-yellow-500"></i> Analyze Now <i className="fas fa-arrow-right ml-2"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid - Premium Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-10">
          {statCards.map((stat, i) => (
            <div
              key={stat.label}
              className="bg-white dark:bg-slate-800 rounded-2xl p-5 lg:p-6 border border-slate-200 dark:border-slate-700 card-hover animate-slide-up"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center`}>
                  <i className={`fas ${stat.icon} bg-gradient-to-r ${stat.color} bg-clip-text text-transparent text-xl`}></i>
                </div>
              </div>
              <div className={`text-3xl lg:text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                {stat.value}
              </div>
              <div className="text-slate-500 dark:text-slate-400 text-sm font-medium mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 mb-8 w-fit">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${activeTab === 'overview'
              ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
          >
            <i className="fas fa-chart-bar mr-2"></i> Overview
          </button>
          <button
            onClick={() => setActiveTab('bookmarks')}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center ${activeTab === 'bookmarks'
              ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
          >
            <i className="fas fa-bookmark mr-2"></i> Saved
            {bookmarks.length > 0 && (
              <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">{bookmarks.length}</span>
            )}
          </button>
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Performance Trend Chart */}
            {attempts.length > 0 && !loading && (
              <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-glass border border-slate-200 dark:border-slate-700 p-6 mb-8 animate-slide-up">
                <h3 className="font-display font-bold text-lg text-slate-800 dark:text-white flex items-center mb-6">
                  <i className="fas fa-chart-line mr-3 text-orange-500"></i> Performance Trend
                </h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="scoreColor" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f85a40" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#f85a40" stopOpacity={0.0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" className="dark:stroke-slate-700" />
                      <XAxis dataKey="date" stroke="#94a3b8" tickLine={false} style={{ fontSize: '11px' }} />
                      <YAxis stroke="#94a3b8" tickLine={false} domain={[0, 100]} unit="%" style={{ fontSize: '11px' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                          borderRadius: '12px', 
                          border: '1px solid #cbd5e1', 
                          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' 
                        }} 
                      />
                      <Area 
                        type="monotone" 
                        dataKey="percentage" 
                        name="Score"
                        stroke="#f85a40" 
                        strokeWidth={3} 
                        fillOpacity={1} 
                        fill="url(#scoreColor)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* History Table */}
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-glass border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="px-6 lg:px-8 py-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex justify-between items-center">
                <h3 className="font-display font-bold text-lg text-slate-800 dark:text-white flex items-center">
                  <i className="fas fa-history mr-3 text-slate-400"></i> Recent Attempts
                </h3>
                <span className="text-sm text-slate-500 dark:text-slate-400">{attempts.length} tests</span>
              </div>

              {loading ? (
                <div className="p-12">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-700 border-t-orange-500 rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-400">Loading your history...</p>
                  </div>
                </div>
              ) : attempts.length === 0 ? (
                <div className="p-16 text-center">
                  <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-6 text-slate-400 dark:text-slate-500 text-3xl">
                    <i className="fas fa-clipboard-list"></i>
                  </div>
                  <h4 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">No tests taken yet</h4>
                  <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-6">Start your first mock test and your performance history will appear here.</p>
                  <button onClick={onStartExam} className="btn-primary text-white px-6 py-3 rounded-xl font-bold">
                    <i className="fas fa-play mr-2"></i> Take Your First Test
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 text-sm">
                        <th className="px-6 lg:px-8 py-4 font-semibold">Test Name</th>
                        <th className="px-6 py-4 font-semibold">Date</th>
                        <th className="px-6 py-4 font-semibold">Score</th>
                        <th className="px-6 py-4 font-semibold">Accuracy</th>
                        <th className="px-6 py-4 font-semibold text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {attempts.map((attempt, i) => {
                        const accuracy = Math.round((attempt.correctCount / (attempt.correctCount + attempt.wrongCount || 1)) * 100);
                        return (
                          <tr
                            key={attempt.id}
                            className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors animate-fade-in"
                            style={{ animationDelay: `${i * 0.05}s` }}
                          >
                            <td className="px-6 lg:px-8 py-5">
                              <div className="font-semibold text-slate-800 dark:text-white">{attempt.examTitle}</div>
                              <div className="text-xs text-slate-400 mt-0.5">{attempt.totalQuestions} questions</div>
                            </td>
                            <td className="px-6 py-5 text-slate-500 dark:text-slate-400 text-sm">
                              {new Date(attempt.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td className="px-6 py-5">
                              <span className="font-bold text-slate-800 dark:text-white">{attempt.score.toFixed(1)}</span>
                              <span className="text-xs text-slate-400 ml-1">/ {attempt.totalQuestions * 2}</span>
                            </td>
                            <td className="px-6 py-5">
                              <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${accuracy > 80 ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' :
                                accuracy > 50 ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400' :
                                  'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'
                                }`}>
                                {accuracy}%
                              </span>
                            </td>
                            <td className="px-6 py-5 text-right">
                              <button className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 text-sm font-semibold inline-flex items-center group">
                                View <i className="fas fa-arrow-right ml-1 group-hover:translate-x-1 transition-transform"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'bookmarks' && (
          <div className="space-y-6">
            {loading ? (
              <div className="p-12 text-center">
                <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-700 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-400">Loading bookmarks...</p>
              </div>
            ) : bookmarks.length === 0 ? (
              <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-glass border border-slate-200 dark:border-slate-700 p-16 text-center">
                <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-6 text-slate-400 dark:text-slate-500 text-3xl">
                  <i className="far fa-bookmark"></i>
                </div>
                <h4 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">No saved questions</h4>
                <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto">Bookmark tricky questions from your test results to review them here.</p>
              </div>
            ) : (
              <>
                {/* Bookmarks Quiz Launcher */}
                <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 dark:from-orange-950/20 dark:to-red-950/20 border border-orange-200/50 dark:border-orange-900/30 rounded-3xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-2xl flex items-center justify-center text-xl shadow-lg shadow-orange-500/25">
                      <i className="fas fa-graduation-cap"></i>
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-slate-800 dark:text-white text-base">Revision Practice Mode</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">Test yourself on all your {bookmarks.length} saved questions.</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLaunchBookmarksPractice}
                    className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-xl text-sm transition-all shadow-md shadow-orange-500/15 hover:opacity-95"
                  >
                    <i className="fas fa-play mr-2"></i> Practice Bookmarks
                  </button>
                </div>

                {bookmarks.map((b, i) => (
                <div
                  key={b.id}
                  className="bg-white dark:bg-slate-800 rounded-3xl shadow-glass border border-slate-200 dark:border-slate-700 p-6 lg:p-8 relative group animate-slide-up"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <button
                    onClick={() => handleRemoveBookmark(b.questionData, b.examId)}
                    className="absolute top-6 right-6 w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/50 dark:hover:text-red-400 flex items-center justify-center transition-all"
                    title="Remove Bookmark"
                  >
                    <i className="fas fa-trash text-sm"></i>
                  </button>
                  <div className="pr-16">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className={`text-xs px-3 py-1 rounded-full font-bold ${b.questionData.difficulty?.toLowerCase() === 'easy' ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' :
                        b.questionData.difficulty?.toLowerCase() === 'hard' ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' :
                          'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-400'
                        }`}>
                        {(b.questionData.difficulty || 'medium').toUpperCase()}
                      </span>
                      <span className="text-slate-400 dark:text-slate-500 text-sm">
                        <i className="far fa-calendar mr-1"></i>
                        Added {new Date(b.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-5">{b.questionData.text}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                      {b.questionData.options.map(opt => (
                        <div
                          key={opt.id}
                          className={`p-4 rounded-xl text-sm border-2 transition-all ${opt.id === b.questionData.correctOptionId
                            ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-800 dark:text-green-300'
                            : 'bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                            }`}
                        >
                          <span className={`font-bold mr-2 ${opt.id === b.questionData.correctOptionId ? 'text-green-600 dark:text-green-400' : ''}`}>
                            {opt.id === b.questionData.correctOptionId && <i className="fas fa-check-circle mr-1"></i>}
                            {opt.id})
                          </span>
                          {opt.text}
                        </div>
                      ))}
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-900/50 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                        <i className="fas fa-lightbulb text-yellow-500 mr-2"></i> Explanation
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {b.questionData.explanation}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </>
            )}
          </div>
        )}

        {/* AI Analysis Modal */}
        {showAnalysis && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-glass-lg w-full max-w-2xl overflow-hidden relative animate-scale-in">
              <button
                onClick={() => setShowAnalysis(false)}
                className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center justify-center transition-colors z-10"
              >
                <i className="fas fa-times"></i>
              </button>

              <div className="p-8">
                {analyzing ? (
                  <div className="flex flex-col items-center justify-center h-[350px] text-center">
                    <div className="w-20 h-20 border-4 border-slate-100 dark:border-slate-700 border-t-purple-600 rounded-full animate-spin mb-6"></div>
                    <h3 className="font-display text-xl font-bold text-slate-800 dark:text-white">Analyzing Performance...</h3>
                    <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-sm">
                      AI is reviewing your last {Math.min(attempts.length, 10)} test attempts to find your strengths and weaknesses.
                    </p>
                  </div>
                ) : analysisData ? (
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl text-white flex items-center justify-center text-2xl mr-4 shadow-lg shadow-purple-500/30">
                        <i className="fas fa-brain"></i>
                      </div>
                      <div>
                        <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white">Performance Insights</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Powered by ThrustED AI</p>
                      </div>
                    </div>

                    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                      {/* Motivation */}
                      <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white p-5 rounded-2xl shadow-lg">
                        <i className="fas fa-quote-left opacity-50 mr-2"></i>
                        <span className="font-medium italic">{analysisData.motivation}</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-green-50 dark:bg-green-900/30 p-5 rounded-2xl border border-green-200 dark:border-green-800">
                          <h4 className="font-display font-bold text-green-800 dark:text-green-300 mb-4 flex items-center">
                            <i className="fas fa-check-circle mr-2"></i> Strengths
                          </h4>
                          <ul className="space-y-3 text-sm text-green-900 dark:text-green-200">
                            {analysisData.strengths.map((item, i) => (
                              <li key={i} className="flex items-start"><span className="mr-2">•</span> {item}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/30 p-5 rounded-2xl border border-red-200 dark:border-red-800">
                          <h4 className="font-display font-bold text-red-800 dark:text-red-300 mb-4 flex items-center">
                            <i className="fas fa-exclamation-circle mr-2"></i> Areas to Improve
                          </h4>
                          <ul className="space-y-3 text-sm text-red-900 dark:text-red-200">
                            {analysisData.weaknesses.map((item, i) => (
                              <li key={i} className="flex items-start"><span className="mr-2">•</span> {item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="bg-blue-50 dark:bg-blue-900/30 p-6 rounded-2xl border border-blue-200 dark:border-blue-800">
                        <h4 className="font-display font-bold text-blue-800 dark:text-blue-300 mb-4 flex items-center">
                          <i className="fas fa-lightbulb mr-2"></i> Recommended Study Plan
                        </h4>
                        <ul className="space-y-4 text-sm text-blue-900 dark:text-blue-200">
                          {analysisData.recommendations.map((item, i) => (
                            <li key={i} className="flex items-start">
                              <span className="bg-blue-200 dark:bg-blue-700 text-blue-800 dark:text-blue-100 text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full mr-3 mt-0.5 flex-shrink-0">{i + 1}</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Action buttons */}
                      <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700 flex gap-4">
                        <button
                          onClick={() => {
                            setShowAnalysis(false);
                            if (onStartCustomExam) onStartCustomExam();
                          }}
                          className="w-full py-3.5 bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-95 text-white rounded-xl font-bold shadow-lg shadow-purple-500/25 transition-all text-center flex items-center justify-center gap-2"
                        >
                          <i className="fas fa-magic"></i> Launch Custom Test Generator
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-red-500 py-12">
                    <i className="fas fa-exclamation-triangle text-4xl mb-4"></i>
                    <p>Analysis failed. Please try again later.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;